import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Users, Waves, DumbbellIcon, Scan, Shirt, UserPlus, Clock, Mail, ChevronRight, Sparkles, Star, Snowflake, Wind } from 'lucide-react';

const tiers = [
  {
    name: 'GATEWAY',
    price: '44.50',
    flexPrice: '49.50',
    period: 'per week',
    description: 'Best value membership',
    features: [
      'Full gym access',
      'Group fitness classes',
      'Wellness facilities',
      'Sauna & steam room',
      'Ice bath, sauna & steam room',
      'Complimentary towel service',
      '1x 60 minute personal training session',
      'Free fitness assessment',
      '6-month contract',
      'No joining fee or key tag fee'
    ],
    flexFeatures: [
      'No lock-in contract',
      'Cancel anytime with 30 days notice',
      'All standard features included'
    ],
    color: 'from-cyan-500/20 to-teal-500/20',
    recommended: true,
    icon: Star,
    tagline: 'MOST POPULAR',
    basicLink: 'https://thefitnessportal.gymmasteronline.com/portal/membership/c64f870aaf8ca324658ff95df6ca5ee8',
    flexiLink: 'https://thefitnessportal.gymmasteronline.com/portal/membership/2035a3fbf72ef20957c9e71a2b8ebf77'
  },
  {
    name: 'TRANSPORTER',
    price: '79.50',
    flexPrice: '84.50',
    period: 'per week',
    description: 'Shared membership for two people',
    features: [
      'Full gym access for TWO people',
      'Group fitness classes for both',
      'Wellness facilities for both',
      'Sauna & steam room access',
      'Complimentary towel service',
      'Free PT session',
      'Free fitness assessment',
      'Free program design',
      '6-month contract',
      'No joining fee'
    ],
    flexFeatures: [
      'No lock-in contract',
      'Cancel anytime with 30 days notice',
      'All standard features included',
      'Perfect for couples or friends'
    ],
    color: 'from-teal-500/20 to-blue-500/20',
    icon: Users,
    tagline: 'SHARED MEMBERSHIP FOR TWO',
    basicLink: 'https://thefitnessportal.gymmasteronline.com/portal/membership/7733b167b08b28b05328818d2987354b',
    flexiLink: 'https://thefitnessportal.gymmasteronline.com/portal/membership/d9d79fe300b9b2068ca6978cab1a5736'
  }
];

const basicTier = {
  name: 'ENTRY',
  price: '22.50',
  flexPrice: '27.50',
  period: 'per week',
  description: 'Basic gym access',
  features: [
    'Full gym access',
    '1x 30 minute session with a trainer',
    'Luxury changing rooms',
    '12 months commitment required',
    '$49 joining fee + $25 key tag fee'
  ],
  flexFeatures: [
    'No lock-in contract',
    'Cancel anytime with 30 days notice',
    'All standard features included',
    'No long-term commitment'
  ],
  color: 'from-blue-500/20 to-cyan-500/20',
  icon: DumbbellIcon,
  tagline: 'STARTER PACKAGE',
  basicLink: 'https://thefitnessportal.gymmasteronline.com/portal/membership/ff42b3b6e24bddc27d07669f34aa3395',
  flexiLink: 'https://thefitnessportal.gymmasteronline.com/portal/membership/b438be5584c9bce9f6a8783ae1506313'
};

const recoveryMembership = {
  name: 'RECOVERY',
  price: '25',
  flexPrice: '30',
  period: 'per week',
  description: 'Focus on wellness & recovery',
  features: [
    'Wellness facilities only',
    'Ice bath therapy',
    'Traditional sauna',
    'Steam room access',
    'Recovery equipment',
    'Luxury changing rooms',
    '6 months commitment'
  ],
  flexFeatures: [
    'No lock-in contract',
    'Cancel anytime with 30 days notice',
    'All recovery features included',
    'No long-term commitment'
  ],
  color: 'from-emerald-500/20 to-blue-500/20',
  icon: Waves,
  tagline: 'RECOVERY FOCUSED',
  basicLink: 'https://thefitnessportal.gymmasteronline.com/portal/membership/e139d6a8c93c166bbb8a32615921c862',
  flexiLink: 'https://thefitnessportal.gymmasteronline.com/portal/membership/3bad926e5be9df8fcb0a705fe6102ac7'
};

const additionalBenefits = [
  {
    icon: Scan,
    title: 'Body Analysis',
    description: 'Monthly Tanita body scan included',
    color: 'from-blue-500/20 to-cyan-500/20'
  },
  {
    icon: Shirt,
    title: 'Towel Service',
    description: 'Complimentary towel hire',
    color: 'from-cyan-500/20 to-teal-500/20'
  },
  {
    icon: UserPlus,
    title: 'Guest Passes',
    description: 'Bring a friend once per month',
    color: 'from-teal-500/20 to-emerald-500/20'
  },
  {
    icon: Clock,
    title: 'Flexible Contract',
    description: 'Open term agreement',
    color: 'from-emerald-500/20 to-blue-500/20'
  }
];

export default function MembershipTiers() {
  const [hoveredTier, setHoveredTier] = useState<number | null>(null);

  return (
    <section className="py-20 bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-7xl font-bold mb-4">MEMBERSHIPS</h2>
          <p className="text-xl text-gray-400">Choose your perfect membership plan</p>
        </motion.div>

        {/* Featured Memberships */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {tiers.map((tier, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.8 }}
              className="relative group h-full"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute -inset-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-3xl blur-lg -z-10"
              />
              <div className="relative h-full bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-blue-400/50 shadow-lg shadow-blue-500/20 p-8 flex flex-col">
                <div className="absolute top-4 right-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm font-bold px-4 py-1 rounded-full">
                  {tier.tagline}
                </div>

                <div className="flex items-center space-x-4 mb-8">
                  <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-3 rounded-full">
                    <tier.icon className="w-8 h-8 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-4xl font-bold">{tier.name}</h3>
                    <p className="text-gray-400">{tier.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 mb-8">
                  <div className="bg-white/5 rounded-xl p-6">
                    <div className="text-sm text-gray-400 mb-2 flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>Standard Term</span>
                    </div>
                    <div className="flex items-baseline">
                      <span className="text-5xl font-bold">${tier.price}</span>
                      <span className="text-gray-400 ml-2">{tier.period}</span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl p-6">
                    <div className="text-sm text-gray-400 mb-2 flex items-center">
                      <Sparkles className="w-4 h-4 mr-2" />
                      <span>Flexible Membership</span>
                    </div>
                    <div className="flex items-baseline">
                      <span className="text-5xl font-bold">${tier.flexPrice}</span>
                      <span className="text-gray-400 ml-2">{tier.period}</span>
                    </div>
                  </div>
                </div>

                <div className="flex-grow">
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold mb-4">Standard Features:</h4>
                    <ul className="space-y-3">
                      {tier.features.map((feature, i) => (
                        <li key={i} className="flex items-center space-x-3">
                          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-cyan-400 mb-4">Flexible Membership Benefits:</h4>
                    <ul className="space-y-3">
                      {tier.flexFeatures.map((feature, i) => (
                        <li key={i} className="flex items-center space-x-3">
                          <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full" />
                          <span className="text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-8">
                  <a
                    href={tier.basicLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center py-4 rounded-full font-bold transition-all duration-300 bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:from-blue-700 hover:to-cyan-700 shadow-lg shadow-blue-500/20 transform hover:scale-105"
                  >
                    STANDARD TERM
                  </a>
                  <a
                    href={tier.flexiLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center py-4 rounded-full font-bold transition-all duration-300 bg-white/10 hover:bg-white/20 transform hover:scale-105"
                  >
                    FLEXI TERM
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Memberships */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {[basicTier, recoveryMembership].map((membership, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2, duration: 0.8 }}
              className="relative group h-full"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${membership.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`} />
              <div className="relative h-full bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 p-8 flex flex-col">
                <div className="absolute top-4 right-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm font-bold px-4 py-1 rounded-full">
                  {membership.tagline}
                </div>

                <div className="flex items-center space-x-4 mb-8">
                  <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-3 rounded-full">
                    <membership.icon className="w-8 h-8 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-4xl font-bold">{membership.name}</h3>
                    <p className="text-gray-400">{membership.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 mb-8">
                  <div className="bg-white/5 rounded-xl p-6">
                    <div className="text-sm text-gray-400 mb-2 flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>Standard Term</span>
                    </div>
                    <div className="flex items-baseline">
                      <span className="text-5xl font-bold">${membership.price}</span>
                      <span className="text-gray-400 ml-2">{membership.period}</span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl p-6">
                    <div className="text-sm text-gray-400 mb-2 flex items-center">
                      <Sparkles className="w-4 h-4 mr-2" />
                      <span>Flexible Membership</span>
                    </div>
                    <div className="flex items-baseline">
                      <span className="text-5xl font-bold">${membership.flexPrice}</span>
                      <span className="text-gray-400 ml-2">{membership.period}</span>
                    </div>
                  </div>
                </div>

                <div className="flex-grow">
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold mb-4">Standard Features:</h4>
                    <ul className="space-y-3">
                      {membership.features.map((feature, i) => (
                        <li key={i} className="flex items-center space-x-3">
                          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-cyan-400 mb-4">Flexible Membership Benefits:</h4>
                    <ul className="space-y-3">
                      {membership.flexFeatures.map((feature, i) => (
                        <li key={i} className="flex items-center space-x-3">
                          <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full" />
                          <span className="text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-8">
                  <a
                    href={membership.basicLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center py-4 rounded-full font-bold transition-all duration-300 bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:from-blue-700 hover:to-cyan-700 shadow-lg shadow-blue-500/20 transform hover:scale-105"
                  >
                    STANDARD TERM
                  </a>
                  <a
                    href={membership.flexiLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center py-4 rounded-full font-bold transition-all duration-300 bg-white/10 hover:bg-white/20 transform hover:scale-105"
                  >
                    FLEXI TERM
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}